// Usage dashboard pieces for the admin console (F-PLAT-6): KPI stat cards with
// a sparkline + period trend, and a dense pixel-mosaic bar chart matching the
// requested reference. Hand-rolled SVG — no chart library, keeping the
// zero-external-dependency + single-token-system invariants (as UsageCharts).
// Monochrome, console light palette.

import { memo, MouseEvent, useCallback, useMemo, useState } from 'react';

import { useMorphedValues } from '../lib/morph';

export interface Point {
  label: string; // x-axis / tooltip label
  value: number;
}

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / mag;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * mag;
}

function strideLabels(points: Point[], keep = 12): (string | null)[] {
  const stride = Math.max(1, Math.ceil(points.length / keep));
  return points.map((p, i) => (i % stride === 0 || i === points.length - 1 ? p.label : null));
}

// Window-over-window trend: mean of the recent half vs the earlier half. Returns
// a signed fraction (+0.2 = up 20%), or null when there isn't enough to compare.
export function trendOf(values: number[]): number | null {
  if (values.length < 4) return null;
  const mid = Math.floor(values.length / 2);
  const mean = (a: number[]) => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0);
  const earlier = mean(values.slice(0, mid));
  const recent = mean(values.slice(mid));
  if (earlier === 0) return recent > 0 ? 1 : null;
  return (recent - earlier) / earlier;
}

const kFmt = (v: number) => (v >= 1000 ? `${Math.round(v / 100) / 10}k` : String(Math.round(v)));

/** Tiny inline bar sparkline for a KPI card. */
export const Sparkline = memo(function Sparkline({ values }: { values: number[] }) {
  // Same tween as the big chart: the whole card should change together.
  const targets = useMemo(() => [values], [values]);
  const morphed = useMorphedValues(targets);
  const drawn = morphed[0] ?? values;
  const max = Math.max(...drawn, 1);
  const n = drawn.length;
  const bw = 100 / Math.max(n, 1);
  return (
    <svg className="bz-spark" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
      {drawn.map((v, i) => {
        const h = (v / max) * 38;
        return (
          <rect
            key={i}
            x={i * bw + bw * 0.2}
            y={40 - h}
            width={bw * 0.6}
            height={Math.max(h, 1)}
            className={i === n - 1 ? 'bz-spark-bar bz-spark-bar-last' : 'bz-spark-bar'}
          />
        );
      })}
    </svg>
  );
});

export const KpiCard = memo(function KpiCard({
  label,
  value,
  unit,
  series,
  trend,
}: {
  label: string;
  value: string | number;
  unit?: string;
  series?: number[];
  trend?: number | null;
}) {
  const up = (trend ?? 0) >= 0;
  return (
    <div className="bz-kpi">
      <div className="bz-kpi-body">
        <div className="bz-kpi-text">
          <span className="bz-kpi-label">{label}</span>
          <div className="bz-kpi-value">
            {value}
            {unit && <span className="bz-kpi-unit">{unit}</span>}
          </div>
        </div>
        {series && series.length > 1 && <Sparkline values={series} />}
      </div>
      {trend != null && (
        <div className="bz-kpi-foot">
          <span className={up ? 'bz-kpi-trend bz-kpi-trend-up' : 'bz-kpi-trend bz-kpi-trend-down'}>
            {up ? '+' : ''}
            {(trend * 100).toFixed(0)}%
          </span>
          <span className="bz-kpi-trend-note">vs earlier</span>
        </div>
      )}
    </div>
  );
});

// ── Dense pixel-mosaic bar chart ────────────────────────────────────────────
const M_W = 900;
const M_H = 300;
const M_PAD = { top: 14, right: 12, bottom: 30, left: 44 };
const ROWS = 18;

export const MosaicChart = memo(function MosaicChart({
  points,
  title,
  seriesLabel = 'Meetings',
  format,
}: {
  points: Point[];
  title: string;
  seriesLabel?: string;
  format?: (v: number) => string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const onEnter = useCallback((e: MouseEvent<SVGRectElement>) => {
    setHover(Number(e.currentTarget.dataset.i));
  }, []);
  const onLeave = useCallback(() => setHover(null), []);

  const max = niceMax(Math.max(...points.map((p) => p.value), 0));
  const iw = M_W - M_PAD.left - M_PAD.right;
  const ih = M_H - M_PAD.top - M_PAD.bottom;
  const cols = Math.max(points.length, 1);
  const colW = iw / cols;
  const rowH = ih / ROWS;
  const gx = Math.min(2.5, colW * 0.16);
  const gy = Math.min(2.5, rowH * 0.18);
  const cellW = colW - gx;
  const cellH = rowH - gy;
  const fmt = format ?? kFmt;
  const labels = strideLabels(points);
  const baseY = M_PAD.top + ih;
  const yTicks = Array.from({ length: 7 }, (_, i) => (max * i) / 6);
  // Dot separators between labelled columns (JAN • FEB • …).
  const labelledX = labels
    .map((l, i) => (l ? M_PAD.left + i * colW + colW / 2 : null))
    .filter((x): x is number => x != null);

  const active = hover != null ? points[hover] : null;
  const hoverCx = hover != null ? M_PAD.left + hover * colW + colW / 2 : 0;
  const hoverTopY = active ? baseY - (active.value / max) * ih : 0;
  const tipStyle = useMemo(
    () => ({ left: `${(hoverCx / M_W) * 100}%`, top: `${(hoverTopY / M_H) * 100}%` }),
    [hoverCx, hoverTopY],
  );

  return (
    <div className="bz-mosaic" role="img" aria-label={title}>
      <svg viewBox={`0 0 ${M_W} ${M_H}`} className="bz-mosaic-svg">
        {/* y ticks: label + leader dot */}
        {yTicks.map((t, i) => {
          const y = baseY - (t / max) * ih;
          return (
            <g key={`y${i}`}>
              <text x={M_PAD.left - 13} y={y + 3.5} className="bz-mosaic-ylabel">
                {fmt(t)}
              </text>
              <circle cx={M_PAD.left - 5} cy={y} r={1.5} className="bz-mosaic-ydot" />
            </g>
          );
        })}
        {/* dense grid: every cell drawn (faint empties + filled marks) */}
        {points.map((p, i) => {
          const filled = max > 0 ? Math.round((p.value / max) * ROWS) : 0;
          const cx = M_PAD.left + i * colW + gx / 2;
          return (
            <g key={i}>
              {Array.from({ length: ROWS }, (_, r) => {
                const y = baseY - (r + 1) * rowH + gy / 2;
                return (
                  <rect
                    key={r}
                    x={cx}
                    y={y}
                    width={cellW}
                    height={cellH}
                    className={r < filled ? 'bz-mcell bz-mcell-on' : 'bz-mcell'}
                  />
                );
              })}
            </g>
          );
        })}
        {/* hover: dotted guide + a dot at the bar top */}
        {active && (
          <g>
            <line x1={hoverCx} y1={M_PAD.top} x2={hoverCx} y2={baseY} className="bz-mosaic-guide" />
            <circle cx={hoverCx} cy={hoverTopY} r={5} className="bz-mosaic-dot" />
          </g>
        )}
        {/* x labels + dot separators */}
        {labelledX.map((x, i) =>
          i < labelledX.length - 1 ? (
            <circle key={`sep${i}`} cx={(x + labelledX[i + 1]!) / 2} cy={M_H - 13} r={1.4} className="bz-mosaic-xdot" />
          ) : null,
        )}
        {points.map((p, i) =>
          labels[i] ? (
            <text key={`x${i}`} x={M_PAD.left + i * colW + colW / 2} y={M_H - 9} className="bz-mosaic-xlabel">
              {labels[i]}
            </text>
          ) : null,
        )}
        {/* transparent hover targets, one per column */}
        {points.map((p, i) => (
          <rect
            key={`h${i}`}
            data-i={i}
            x={M_PAD.left + i * colW}
            y={M_PAD.top}
            width={colW}
            height={ih}
            fill="transparent"
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
          />
        ))}
      </svg>
      {active && (
        <div className="bz-mosaic-tip" style={tipStyle}>
          <div className="bz-mosaic-tip-label">{active.label}</div>
          <div className="bz-mosaic-tip-row">
            <span className="bz-mosaic-tip-dot" />
            <span className="bz-mosaic-tip-name">{seriesLabel}</span>
            <span className="bz-mosaic-tip-value">{fmt(active.value)}</span>
          </div>
        </div>
      )}
    </div>
  );
});

// ── Interactive area chart (system metrics) ─────────────────────────────────
const L_W = 560;
const L_H = 180;
const L_PAD = { top: 12, right: 14, bottom: 24, left: 46 };

export const LineChart = memo(function LineChart({
  points,
  format,
}: {
  points: Point[];
  format?: (v: number) => string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const onEnter = useCallback((e: MouseEvent<SVGRectElement>) => {
    setHover(Number(e.currentTarget.dataset.i));
  }, []);
  const onLeave = useCallback(() => setHover(null), []);

  const max = niceMax(Math.max(...points.map((p) => p.value), 0));
  const iw = L_W - L_PAD.left - L_PAD.right;
  const ih = L_H - L_PAD.top - L_PAD.bottom;
  const n = points.length;
  const step = n > 1 ? iw / (n - 1) : 0;
  const baseY = L_PAD.top + ih;
  const fmt = format ?? kFmt;
  const labels = strideLabels(points, 5);
  const yTicks = Array.from({ length: 4 }, (_, i) => (max * i) / 3);
  const xy = points.map((p, i) => {
    const x = L_PAD.left + i * step;
    const y = baseY - (max > 0 ? (p.value / max) * ih : 0);
    return [x, y] as const;
  });
  const line = xy.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = xy.length
    ? `${line} L${xy[n - 1]![0].toFixed(1)},${baseY} L${xy[0]![0].toFixed(1)},${baseY} Z`
    : '';

  const active = hover != null ? points[hover] : null;
  const ax = hover != null ? xy[hover]![0] : 0;
  const ay = hover != null ? xy[hover]![1] : 0;
  const tipStyle = useMemo(
    () => ({ left: `${(ax / L_W) * 100}%`, top: `${(ay / L_H) * 100}%` }),
    [ax, ay],
  );

  return (
    <div className="bz-line" role="img">
      <svg viewBox={`0 0 ${L_W} ${L_H}`} className="bz-line-svg">
        {yTicks.map((t, i) => {
          const y = baseY - (t / max) * ih;
          return (
            <g key={`y${i}`}>
              <line x1={L_PAD.left} y1={y} x2={L_W - L_PAD.right} y2={y} className="bz-line-grid" />
              <text x={L_PAD.left - 10} y={y + 3} className="bz-mosaic-ylabel">
                {fmt(t)}
              </text>
            </g>
          );
        })}
        {area && <path d={area} className="bz-line-area" />}
        {line && <path d={line} className="bz-line-stroke" />}
        {active && (
          <g>
            <line x1={ax} y1={L_PAD.top} x2={ax} y2={baseY} className="bz-mosaic-guide" />
            <circle cx={ax} cy={ay} r={4.5} className="bz-line-dot" />
          </g>
        )}
        {points.map((p, i) =>
          labels[i] ? (
            <text key={`x${i}`} x={xy[i]![0]} y={L_H - 7} className="bz-mosaic-xlabel">
              {labels[i]}
            </text>
          ) : null,
        )}
        {points.map((p, i) => (
          <rect
            key={`h${i}`}
            data-i={i}
            x={L_PAD.left + (i - 0.5) * step}
            y={L_PAD.top}
            width={step || iw}
            height={ih}
            fill="transparent"
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
          />
        ))}
      </svg>
      {active && (
        <div className="bz-mosaic-tip" style={tipStyle}>
          <div className="bz-mosaic-tip-label">{active.label}</div>
          <div className="bz-mosaic-tip-row">
            <span className="bz-mosaic-tip-value">{fmt(active.value)}</span>
          </div>
        </div>
      )}
    </div>
  );
});
